import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useState, type FC, type PropsWithChildren } from 'react';
import { useViewport } from '@sbt-web/hooks';
import { GeoEntry } from '@sbt-web/network/types';
import { Button, Icon, IconSize } from '@sbt-web/ui';
import { TuttaItalia } from '@sbt-web/utils';
import { useAppDispatch, useAppSelector } from '@/reducers/common';
import { updateGeo } from '@/reducers/search';
import { getOrCreatePulse } from '@/tools/tracking/utils';
import ownClasses from './NationwideSearchExpander.module.scss';
const searchExpansionTracking = {
  type: 'Click',
  page: {
    get '@id'() {
      return `sdrn:subito:page:${window.location.href}`;
    },
    '@type': 'Page',
    pageType: 'listing'
  },
  target: {
    id: 'listing',
    type: 'Listing'
  },
  object: {
    id: 'shipping-filter-location-expander',
    type: 'UIElement',
    name: 'ShippingFilter',
    get url() {
      return window.location.href;
    }
  }
};
const locationIsAllOfItaly = (location: string) => location === TuttaItalia.value;
const nameFromGeo = (geo: GeoEntry, includeNearbyRegions: boolean): string => {
  const name = geo.town?.value ?? geo.city?.value ?? geo.region.value;
  if (includeNearbyRegions) {
    return `${geo.region.value} e regioni vicine`;
  } else {
    return name;
  }
};
type MessageProps = PropsWithChildren<{
  version: 'cta' | 'thanks';
  id?: string;
}>;
const Message: FC<MessageProps> = t0 => {
  const $ = _c(10);
  const {
    version,
    id,
    children
  } = t0;
  const t1 = version === "cta" ? "Info" : "Check";
  let t2;
  if ($[0] !== t1) {
    t2 = <div className={ownClasses.bubble}><Icon name={t1} iconSize={IconSize.MD} /></div>;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== children) {
    t3 = <div>{children}</div>;
    $[2] = children;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t2 || $[5] !== t3) {
    t4 = <div className={ownClasses["search-expansion-prompt"]}>{t2}{t3}</div>;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== id || $[8] !== t4) {
    t5 = <div id={id}>{t4}</div>;
    $[7] = id;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
};
type NationwideSearchExpanderProps = {
  id?: string;
};
export const NationwideSearchExpander: FC<NationwideSearchExpanderProps> = t0 => {
  const $ = _c(22);
  const {
    id
  } = t0;
  const dispatch = useAppDispatch();
  const includeShippableOnly = useAppSelector(_temp);
  const location = useAppSelector(_temp2);
  const {
    isMobile
  } = useViewport();
  const isInAllOfItaly = locationIsAllOfItaly(location);
  const showSwitch = includeShippableOnly && !isInAllOfItaly;
  const [switchUsed, setSwitchUsed] = useState(false);
  let t1;
  let t2;
  if ($[0] !== includeShippableOnly || $[1] !== location) {
    t1 = () => {
      if (!locationIsAllOfItaly(location) || !includeShippableOnly) {
        setSwitchUsed(false);
      }
    };
    t2 = [location, includeShippableOnly, setSwitchUsed];
    $[0] = includeShippableOnly;
    $[1] = location;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  const [nonNationalLocation, setNonNationalLocation] = useState(null);
  let t3;
  if ($[4] !== dispatch || $[5] !== location) {
    t3 = () => {
      getOrCreatePulse()?.queueEvent(searchExpansionTracking);
      setNonNationalLocation(location);
      setSwitchUsed(true);
      dispatch(updateGeo({
        region: TuttaItalia
      }));
    };
    $[4] = dispatch;
    $[5] = location;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const expandSearch = t3;
  if (switchUsed) {
    if (isMobile) {
      const t4 = `small book ${isMobile ? "caption" : "body-text"}`;
      let t5;
      if ($[7] !== nonNationalLocation || $[8] !== t4) {
        t5 = <p className={t4}>Ottimo! Hai cambiato il luogo di ricerca da "{nonNationalLocation}" a "Tutta Italia".</p>;
        $[7] = nonNationalLocation;
        $[8] = t4;
        $[9] = t5;
      } else {
        t5 = $[9];
      }
      let t6;
      if ($[10] !== id || $[11] !== t5) {
        t6 = <Message version="thanks" id={id}>{t5}</Message>;
        $[10] = id;
        $[11] = t5;
        $[12] = t6;
      } else {
        t6 = $[12];
      }
      return t6;
    } else {
      return null;
    }
  } else {
    if (!showSwitch) {
      return null;
    }
  }
  const t4 = `small book ${isMobile ? "caption" : "body-text"}`;
  let t5;
  if ($[13] !== t4) {
    t5 = <p className={t4}>Cerca in tutta Italia e non lasciarti scappare l'affare. Con la spedizione ricevi l'oggetto che desideri ovunque tu sia.</p>;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = ["search-expander", ownClasses["expand-search"]];
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== expandSearch) {
    t7 = <Button design="text" slim={true} size="small" onClick={expandSearch} classes={t6}>Cerca in Tutta Italia</Button>;
    $[16] = expandSearch;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== id || $[19] !== t5 || $[20] !== t7) {
    t8 = <Message version="cta" id={id}>{t5}{t7}</Message>;
    $[18] = id;
    $[19] = t5;
    $[20] = t7;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  return t8;
};
function _temp(t0) {
  const {
    search
  } = t0;
  return search.includeShippableOnly;
}
function _temp2(t0) {
  const {
    search: search_0
  } = t0;
  return nameFromGeo(search_0.geo, search_0.searchNearRegions ?? false);
}